/**
 * Represents the possible types of expression tokens.
 * @enum {string}
 */
export enum ExpressionTokenType {
  BRANCH = 'BRANCH', // if
  FIELD = 'FIELD',
  OPERATOR = 'OPERATOR', // ==, >=, <=, !=
  VALUE = 'VALUE', // manual input value
  CONDITION = 'CONDITION', // and, or, then
}

/**
 * Represents the possible conditions for expression tokens.
 * @enum {string}
 */
export enum ExpressionCondition {
  AND = 'AND',
  OR = 'OR'
}

/**
 * Represents an expression token.
 * @interface
 */
export interface ExpressionToken {
  /**
   * The type of the expression token.
   * @type {ExpressionTokenType}
   */
  type: ExpressionTokenType;

  /**
   * The value of the expression token.
   * @type {string}
   */
  value: string;
}

/**
 * Represents an expression.
 * @type {ExpressionToken[]}
 */
export type Expression = ExpressionToken[];

/**
 * Represents the type of a schema field.
 * @interface
 */
export interface SchemaFieldType {
  /**
   * The name of the schema field.
   * @type {string}
   */
  name: string;

  /**
   * The type of the schema field.
   * @type {SchemaFieldType | string | Array<SchemaFieldType | string>}
   */
  type: SchemaFieldType | string | Array<SchemaFieldType | string>;

  /**
   * The fields of the schema field (optional).
   * @type {SchemaFieldType[] | undefined}
   */
  fields?: SchemaFieldType[];
}

/**
 * Represents an expression field.
 * @interface
 */
export interface ExpressionField {
  /**
   * The field name.
   * @type {string}
   */
  field: string;

  /**
   * The field type.
   * @type {string}
   */
  type: string;
}

/**
 * Represents the data for an expression builder.
 * @interface
 */
export interface ExpressionBuilderData {
  /**
   * The expression string.
   * @type {string | undefined}
   */
  expressionStr?: string;

  /**
   * The expression builder string.
   * @type {string | undefined}
   */
  expressionBuilderStr?: string;

  /**
   * The event associated with the expression.
   * @type {string}
   */
  event: string;

  eventVersion: number;

  /**
   * The ID of the node associated with the expression.
   * @type {string}
   */
  toNodeId: string;

  fromNodeId: string;
}

/**
 * Represents the props for an expression builder component.
 * @interface
 */
export interface ExpressionBuilderProps {
  /**
   * The expression data.
   * @type {ExpressionBuilderData | undefined}
   */
  expressionData?: ExpressionBuilderData;

  /**
   * Indicates whether the expression builder is shown.
   * @type {boolean}
   */
  show: boolean;

  /**
   * A callback function for when expressions are updated.
   * @type {(expressions: ExpressionBuilderData) => void}
   */
  onExpressionsUpdated: (expressions: ExpressionBuilderData) => void;

  /**
   * A callback function for when the expression builder is closed.
   * @type {() => void}
   */
  onClose: () => void;

  /**
   * If Journey/Canvas is in read-only/view mode
   */
  readOnly?: boolean;
}

export const branchValues = ['IF'];

/**
 * An array of branch values, sorted in descending order based on their length.
 * @type {string[]}
 */
export const branchValuesSorted = [...branchValues].sort((a, b) => b.length - a.length);

// separate array to maintain the order of operator, object keys don't maintain their order
export const datatypeOperatorValuesList: { [key: string]: string[] } = {
  int: ['==', '!=', '>', '<', '>=', '<='],
  long: ['==', '!=', '>', '<', '>=', '<='],
  float: ['==', '!=', '>', '<', '>=', '<='],
  double: ['==', '!=', '>', '<', '>=', '<='],
  string: ['==', '!=', 'CONTAINS', 'NOT CONTAINS'],
  boolean: ['==', '!='],
  array: ['==', '!=', 'CONTAINS', 'NOT CONTAINS'],
};

export const operatorDisplayMap: { [key: string]: string } = {
  '==': 'Equal to (==)',
  '!=': 'Not equal to (!=)',
  '>': 'Greater than (>)',
  '<': 'Less than (<)',
  '>=': 'Greater than or equals to (>=)',
  '<=': 'Less than or equals to (<=)',
  CONTAINS: 'Contains',
  'NOT CONTAINS': 'Not Contains',
};

export const datatypeOperatorValuesMap: { [key: string]: { [key: string]: string } } = {
  int: {
    '==': 'payload.{{field}}.toString().equals({{value}}.toString())',
    '!=': '!payload.{{field}}.toString().equals({{value}}.toString())',
    '>': 'payload.{{field}} > {{value}}',
    '<': 'payload.{{field}} < {{value}}',
    '>=': 'payload.{{field}} >= {{value}}',
    '<=': 'payload.{{field}} <= {{value}}',
  },
  long: {
    '==': 'payload.{{field}}.toString().equals({{value}}.toString())',
    '!=': '!payload.{{field}}.toString().equals({{value}}.toString())',
    '>': 'payload.{{field}} > {{value}}',
    '<': 'payload.{{field}} < {{value}}',
    '>=': 'payload.{{field}} >= {{value}}',
    '<=': 'payload.{{field}} <= {{value}}',
  },
  float: {
    '==': 'payload.{{field}}.toString().equals({{value}}.toString())',
    '!=': '!payload.{{field}}.toString().equals({{value}}.toString())',
    '>': 'payload.{{field}} > {{value}}',
    '<': 'payload.{{field}} < {{value}}',
    '>=': 'payload.{{field}} >= {{value}}',
    '<=': 'payload.{{field}} <= {{value}}',
  },
  double: {
    '==': 'payload.{{field}}.toString().equals({{value}}.toString())',
    '!=': '!payload.{{field}}.toString().equals({{value}}.toString())',
    '>': 'payload.{{field}} > {{value}}',
    '<': 'payload.{{field}} < {{value}}',
    '>=': 'payload.{{field}} >= {{value}}',
    '<=': 'payload.{{field}} <= {{value}}',
  },
  string: {
    '==': 'payload.{{field}}.equalsIgnoreCase({{value}})',
    '!=': '!payload.{{field}}.equalsIgnoreCase({{value}})',
    CONTAINS: 'payload.{{field}}.contains({{value}})',
    'NOT CONTAINS': '!payload.{{field}}.contains({{value}})',
  },
  boolean: {
    '==': 'payload.{{field}}.toString().equalsIgnoreCase({{value}}.toString())',
    '!=': '!payload.{{field}}.toString().equalsIgnoreCase({{value}}.toString())',
  },
  array: {
    '==': 'payload.{{field}}.toString().equalsIgnoreCase({{value}}.toString())',
    '!=': '!payload.{{field}}.toString().equalsIgnoreCase({{value}}.toString())',
    CONTAINS: 'payload.{{field}}.toString().contains({{value}}.toString())',
    'NOT CONTAINS': '!payload.{{field}}.toString().contains({{value}}.toString())',
  },
};

export const conditionValues = [ExpressionCondition.AND, ExpressionCondition.OR];

/**
 * An array of condition values, sorted in descending order based on their length.
 * @type {string[]}
 */
export const conditionValuesSorted = [...conditionValues].sort((a, b) => b.length - a.length);
